import { Feather, FontAwesome, MaterialIcons } from "@expo/vector-icons";
import Styles from "../../../constants/styles";
import React, { useState } from "react";
import { Alert, Dimensions, Platform, Text, View } from "react-native";
import { useDispatch, useSelector } from "react-redux";
import {
  Button,
  Centered,
  ErrorMessage,
  Input,
  Loading,
  Row,
  StepHeader,
} from "../../../components/all";
import { useNavigation } from "@react-navigation/native";
import { normalize } from "../../../utils/normalize";
import colors from "../../../constants/colors";
import { validateFields, requestOTP, register } from "../../../services/auth";
import { useMutation } from "@tanstack/react-query";
import { setUserToken } from "../../../reducers/auth/user";

import { addFields } from "../../../reducers/auth/register";
import { useCitiesByCountry } from "../../../hooks/useCitiesByCountry";
import { getAdministrativeDivisionLabel } from "../../../constants/countries";

export default function RegisterUser({ route }) {
  const isWeb = Platform.OS === "web";
  const { jobType } = route?.params || {};
  console.log("jobType", jobType);
  const dispatch = useDispatch();
  const { fields } = useSelector((state) => state.register);
  const isFullTime = fields.type == -1;
  const { isDoctor, country } = useSelector((state) => state.user);

  // Fetch states/districts/provinces based on country
  const { data: statesData } = useCitiesByCountry(country || 'MY');
  const divisionLabel = getAdministrativeDivisionLabel(country || 'MY');

  const [showPassword, setShowPassword] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(true);
  const [showPasswordFields, setShowPasswordFields] = useState(false);
  const [focusedInput, setFocusedInput] = useState(null);
  const [allFields, setAllFields] = useState({});
  const [errorFields, setErrorFields] = useState([]);
  const [verifying, setVerifying] = useState(false);

  const navigation = useNavigation();

  const setField = (field, value) =>
    setAllFields({ ...allFields, [field]: value });

  const input = {
    email: "email",
    password: "password",
    confirmPassword: "confirmPassword",
    firstName: "first_name",
    lastName: "last_name",
    department: "department",
    address: "address",
    city: "city",
    postcode: "postcode",
    state: "state",
  };

  const validate = () => {
    let toValidate = ["email"];
    if (isDoctor) {
      toValidate.push("first_name");
      // Address fields are now optional for doctors
    }

    if (showPasswordFields) {
      toValidate = [...toValidate, "password", "confirmPassword"];
      allFields.password !== allFields.confirmPassword &&
        toValidate.push("passwordMatchError");
      allFields.password?.length < 6 && toValidate.push("passwordLengthError");
      // Postcode validation for doctors - only validate if postcode is provided
      // if (isDoctor && allFields.postcode && allFields.postcode.length > 0 && allFields.postcode.length !== 5) {
      //   toValidate.push("postcodeError");
      // }
    }
    allFields.email &&
      !allFields.email?.match(
        /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/
      ) &&
      toValidate.push("emailError");
    const result = toValidate.filter((val) => {
      return allFields[val] === undefined || allFields[val] === "";
    });
    setErrorFields(result);
    console.log("check all fieklds", allFields);
    if (result.length === 0) {
      if (showPasswordFields) {
        setVerifying(true);
        dispatch(
          addFields({
            ...allFields,
            ...fields,
          })
        );
        registerCheck.mutate({
          email: allFields.email,
          password: allFields.password,
          scope: isDoctor ? "doctor" : "clinic_owner",
        });
      } else {
        setShowPasswordFields(true);
      }
    }
  };
  const sendOTP = useMutation(requestOTP, {
    onError: (error) => {
      setVerifying(false);
      alert("Oops! " + error.response.data.message);
    },
    onSuccess: (res) => {
      setVerifying(false);
      dispatch(
        addFields({
          ...allFields,
          nonce: res.data.data.nonce,
        })
      );
      navigation.navigate("ValidateOTPScreen");
    },
  });

  const registerUser = useMutation(register, {
    onError: (error) => {
      if (error.response.data?.message) {
        Alert.alert("Registration Error", error.response.data.message);
        return;
      }
      let formattedError = "";
      Object.map(error.response.data.errors, (v, k) => {
        let errorMessage = v.join("\n");
        formattedError += `\n${capitalizeFirstLetter(k)}\n${errorMessage}\n`;
      });
      Alert.alert("Registration Error", formattedError);
    },
    onSuccess: (data) => {
      dispatch(setUserToken(data.data.token));
      sendOTP.mutate({
        email: allFields.email,
      });
    },
  });

  const registerCheck = useMutation(validateFields, {
    onError: (error) => {
      setVerifying(false);
      Alert.alert(
        "Oops!",
        "This email is already in use, please try to login again with the right password or reset password.",
        [
          {
            text: "Cancel",
            style: "cancel",
          },
          {
            text: "OK",
          },
        ]
      );
    },
    onSuccess: (data) => {
      setVerifying(false);
      if (isDoctor) {
        registerUser.mutate({
          ...fields,
          ...allFields,
          type: isFullTime ? 1 : fields.type,
          last_name: "-",
          scope: "user",
          role: "doctor",
          country: country || "MY", // Include country from Redux
          address: allFields.address,
          city: allFields.city,
          postcode: allFields.postcode,
          state: allFields.state,
        });
      } else {
        navigation.navigate("RegisterLocationScreen", {
          jobType: jobType,
        });
      }
    },
  });
  return (
    <>
      <View
        style={{
          ...Styles.webSmallContainer,
          marginTop: 100,
        }}
      >
        <StepHeader
          label={`Account \nCredentials`}
          icon={<Feather name="credit-card" size={24} color="black" />}
          subtitle={`How can we reach you?`}
        />
        <Centered
          style={{ width: Dimensions.get("window").width > 480 ? 480 : "100%" }}
        >
          {isDoctor ? (
            <>
              <Input
                onChangeText={(val) => {
                  setField(input.firstName, val);
                }}
                autoCapitalize="words"
                value={allFields[input.firstName]}
                placeholder="Full Name"
                containerStyle={{
                  width: "90%",
                  marginTop: 10,
                }}
                icon={<FontAwesome name="user" size={14} color="grey" />}
                onBlur={() => setFocusedInput(null)}
                onFocus={() => {
                  setErrorFields([]);
                  setFocusedInput(input.firstName);
                }}
                focused={focusedInput === input.firstName}
                error={errorFields.includes(input.firstName)}
              />
            </>
          ) : (
            <Input
              onChangeText={(val) => {
                setField(input.lastName, val);
              }}
              autoCapitalize="none"
              value={allFields[input.lastName]}
              icon={<FontAwesome name="hospital-o" size={14} color="grey" />}
              placeholder="Facility Name (E.g. Sunway Hospital)"
              containerStyle={{
                width: "90%",
                marginTop: 10,
              }}
              onBlur={() => setFocusedInput(null)}
              onFocus={() => {
                setErrorFields([]);
                setFocusedInput(input.lastName);
              }}
              focused={focusedInput === input.lastName}
              error={errorFields.includes(input.lastName)}
            />
          )}
          {[101, 102].includes(fields.type) ? (
            <Input
              onChangeText={(val) => {
                setField(input.department, val);
              }}
              autoCapitalize="words"
              value={allFields[input.department]}
              placeholder="Facility Department (E.g. Rehab Centre)"
              containerStyle={{
                width: "90%",
                marginTop: 10,
              }}
              icon={<FontAwesome name="user" size={14} color="grey" />}
              onBlur={() => setFocusedInput(null)}
              onFocus={() => {
                setErrorFields([]);
                setFocusedInput(input.department);
              }}
              focused={focusedInput === input.department}
              error={errorFields.includes(input.department)}
            />
          ) : null}
          {isDoctor ? (
            <>
              <Input
                onChangeText={(val) => {
                  setField(input.address, val);
                }}
                autoCapitalize="words"
                value={allFields[input.address]}
                icon={<FontAwesome name="home" size={14} color="grey" />}
                placeholder="Home Address"
                containerStyle={{ width: "90%", marginTop: 10 }}
                onBlur={() => setFocusedInput(null)}
                onFocus={() => {
                  setErrorFields([]);
                  setFocusedInput(input.address);
                }}
                focused={focusedInput === input.address}
                error={errorFields.includes(input.address)}
              />
              <Input
                onChangeText={(val) => {
                  setField(input.city, val);
                }}
                autoCapitalize="words"
                value={allFields[input.city]}
                icon={<MaterialIcons name="location-city" size={14} color="grey" />}
                placeholder="City/Town"
                containerStyle={{ width: "90%", marginTop: 10 }}
                onBlur={() => setFocusedInput(null)}
                onFocus={() => {
                  setErrorFields([]);
                  setFocusedInput(input.city);
                }}
                focused={focusedInput === input.city}
                error={errorFields.includes(input.city)}
              />
              <Row>
                <Input
                  onChangeText={(val) => {
                    setField(input.postcode, val);
                  }}
                  value={allFields[input.postcode]}
                  icon={<FontAwesome name="location-arrow" size={14} color="grey" />}
                  placeholder="Postal Code"
                  containerStyle={{
                    width: Dimensions.get("window").width > 480 ? 210 : Dimensions.get("window").width / 2.28,
                    marginTop: 10,
                    marginRight: 10,
                  }}
                  keyboardType="numeric"
                  onBlur={() => setFocusedInput(null)}
                  onFocus={() => {
                    setErrorFields([]);
                    setFocusedInput(input.postcode);
                  }}
                  focused={focusedInput === input.postcode}
                  error={errorFields.includes(input.postcode) || errorFields.includes("postcodeError")}
                />
                <Input
                  isPicker
                  onChangeText={(val) => {
                    setField(input.state, val);
                  }}
                  value={allFields[input.state]}
                  placeholder={divisionLabel}
                  containerStyle={{
                    width: Dimensions.get("window").width > 480 ? 210 : Dimensions.get("window").width / 2.28,
                    marginTop: 10,
                  }}
                  onBlur={() => setFocusedInput(null)}
                  onFocus={() => {
                    setErrorFields([]);
                    setFocusedInput(input.state);
                  }}
                  error={errorFields.includes(input.state)}
                  focused={focusedInput === input.state}
                  pickerItems={statesData?.map(state => ({
                    label: state.name,
                    value: state.value
                  })) || []}
                />
              </Row>
            </>
          ) : null}
          <Input
            onChangeText={(val) => {
              setField(input.email, val);
            }}
            autoCapitalize="none"
            value={allFields[input.email]}
            icon={<FontAwesome name="envelope" size={14} color="grey" />}
            placeholder="Email Address"
            containerStyle={{
              width: "90%",
              marginTop: 10,
            }}
            keyboardType={"email-address"}
            onBlur={() => setFocusedInput(null)}
            onFocus={() => {
              setErrorFields([]);
              setFocusedInput(input.email);
            }}
            focused={focusedInput === input.email}
            error={errorFields.includes(input.email)}
          />
          {showPasswordFields ? (
            <>
              <Input
                onChangeText={(val) => {
                  setField(input.password, val);
                }}
                value={allFields[input.password]}
                secureTextEntry={!showPassword}
                icon={<FontAwesome name="lock" size={14} color="grey" />}
                placeholder="Password"
                containerStyle={{ width: "90%", marginTop: 10 }}
                onBlur={() => setFocusedInput(null)}
                onFocus={() => {
                  setErrorFields([]);
                  setFocusedInput(input.password);
                }}
                autoCapitalize="none"
                error={errorFields.includes(input.password)}
                focused={focusedInput === input.password}
                backIcon={
                  <Feather
                    name={showPassword ? "eye-off" : "eye"}
                    size={18}
                    color="grey"
                  />
                }
                onPressBackIcon={() => {
                  setShowPassword(!showPassword);
                }}
              />
              <Input
                onChangeText={(val) => {
                  setField(input.confirmPassword, val);
                }}
                value={allFields[input.confirmPassword]}
                secureTextEntry={!showPassword}
                icon={<FontAwesome name="unlock-alt" size={14} color="grey" />}
                placeholder="Confirm Password"
                containerStyle={{ width: "90%", marginTop: 10 }}
                onBlur={() => setFocusedInput(null)}
                onFocus={() => {
                  setErrorFields([]);
                  setFocusedInput(input.confirmPassword);
                }}
                autoCapitalize="none"
                error={errorFields.includes(input.confirmPassword)}
                focused={focusedInput === input.confirmPassword}
                backIcon={
                  <Feather
                    name={showPassword ? "eye-off" : "eye"}
                    size={18}
                    color="grey"
                  />
                }
                onPressBackIcon={() => {
                  setShowPassword(!showPassword);
                }}
              />
            </>
          ) : null}

          <Row
            centered
            style={{ marginTop: 12, alignSelf: "flex-start", marginLeft: 24 }}
          >
            <MaterialIcons
              name={acceptTerms ? "check-box" : "check-box-outline-blank"}
              size={14}
              color={acceptTerms ? "rgb(72, 92, 242)" : "rgba(0,0,0,0.2)"}
            />
            <Text
              style={{ marginLeft: 8, color: "grey", fontSize: normalize(12) }}
            >
              I have read and agreed to the{" "}
            </Text>
            <Text
              onPress={() => {
                navigation.navigate("TermsConditionScreen");
              }}
              style={{
                color: colors.tertiary,
                textDecorationLine: "underline",
                fontSize: normalize(12),
              }}
            >
              Terms & Conditions
            </Text>
          </Row>
        </Centered>
        <View style={{ margin: 10, marginLeft: 24 }}>
          <ErrorMessage
            visible={
              errorFields.includes(input.phone_number) ||
              errorFields.includes(input.email) ||
              errorFields.includes(input.password) ||
              errorFields.includes(input.confirmPassword) ||
              errorFields.includes(input.firstName)
            }
            label="Please fill in the required fields"
          />
          <ErrorMessage
            visible={errorFields.includes("emailError")}
            label="Please enter a valid email."
          />
          <ErrorMessage
            visible={errorFields.includes("passwordMatchError")}
            label="Passwords do not match."
          />
          <ErrorMessage
            visible={errorFields.includes("passwordLengthError")}
            label="Passwords length must be greater than 6."
          />
          <ErrorMessage
            visible={errorFields.includes("postcodeError")}
            label="Please enter a valid postcode."
          />
        </View>
        <Centered>
          <Row>
            <Button
              label="Back"
              type={"secondary"}
              onPress={() => {
                if (isFullTime) {
                  navigation.pop(2)
                } else {
                  navigation.goBack();
                }
              }}
            />
            <Button label="Submit" type={"primary"} onPress={validate} />
          </Row>
        </Centered>
      </View>
      <Loading visible={verifying && !isWeb} />
    </>
  );
}
